import { defineComponent } from 'vue';
import { v4 as uuidV4 } from 'uuid';
import ModalOverlay from '~/components/common/ModalOverlay.vue';
import shuffleArray from '~/utils/shuffleArray';
const CREDIT_BAND_SURVEY_ID = 14;
const MORTGAGE_QUESTION_ID = 2;
const MORTGAGE_ANSWER_CHOICE_IDS = {
  makingOffers: '6',
  from0to3months: '7',
  from3to6months: '8',
  from6to12months: '9',
  over12months: '10'
};
function getInitialPendingQuestionIds(initialQuestionIds, creditBandQuestion) {
  if (initialQuestionIds.length === 0 && creditBandQuestion) {
    return [CREDIT_BAND_SURVEY_ID];
  }
  return Array.from(initialQuestionIds);
}
export default defineComponent({
  name: 'MemberSurvey',
  components: {
    ModalOverlay
  },
  props: {
    initialQuestionIds: {
      type: Array,
      required: true
    },
    creditBandVariant: {
      type: Number,
      required: true
    },
    creditBandQuestion: {
      type: Boolean,
      default: false
    },
    parentSubmitDisabled: {
      type: Boolean,
      default: false
    },
    // just used for analytics for now
    memberProfile: {
      type: Object,
      required: false
    }
  },
  emits: ['showDisclosure', 'response', 'complete'],
  data() {
    return {
      startingId: this.initialQuestionIds[0],
      // by the time the page loads, question should always be defined
      question: undefined,
      pendingQuestionIds: getInitialPendingQuestionIds(this.initialQuestionIds, this.creditBandQuestion),
      disclosureOpen: false,
      // NOTE: selected ends up being sorted by ID which is good, but I'm not sure why, maybe $set sorts the object keys?
      selected: {},
      freeformResponses: {},
      processing: false
    };
  },
  async fetch() {
    const fetchRes = await this.$axios.$get(`/api/member-survey/${this.pendingQuestionIds[0]}`);
    if (fetchRes.shuffle) {
      fetchRes.choices = shuffleArray(fetchRes.choices, choice => choice.title === 'Other');
    }
    // override credit goals description
    if (fetchRes.id === CREDIT_BAND_SURVEY_ID && this.creditBandVariant === 2) {
      fetchRes.description = 'This won’t impact your application with us.';
    }
    this.question = fetchRes;
    this.pendingQuestionIds = this.pendingQuestionIds.slice(1);
  },
  computed: {
    isCreditGoalsFirstQuestion() {
      return this.question.id === 1;
    },
    header() {
      if (this.question.id === 1) {
        return "What's your credit goal?";
      } else if (this.question.id === 14) {
        // credit band question
        return 'Your credit journey begins today';
      } else {
        return 'Keep an eye on your credit goals';
      }
    },
    submitDisabled() {
      return this.processing || this.parentSubmitDisabled || Object.keys(this.selected).length === 0 ||
      // check if there are any freeform choices that the user didn't enter a response for
      Object.keys(this.selected).some(key => {
        var _a;
        return ((_a = this.choiceById(key)) === null || _a === void 0 ? void 0 : _a.freeform) && !this.freeformResponses[key];
      });
    }
  },
  methods: {
    choiceById(key) {
      return this.question.choices.find(c => c.id === parseInt(key));
    },
    setResponse(id, value) {
      if (value === false) {
        this.$delete(this.selected, id);
        this.$delete(this.freeformResponses, id);
      } else {
        // for non-multiselect, clear responses when changing selection
        if (value === true && !this.question.multiselect) {
          this.selected = {};
          this.freeformResponses = {};
        }
        this.$set(this.selected, id, value);
      }
    },
    /**
     * @param skipComplete skip emitting the `complete` event when all questions are answered
     * @returns Whether the survey is complete (no more pending questions)
     */
    async submit(skipComplete) {
      var _a;
      if (this.parentSubmitDisabled || this.submitDisabled) {
        return false;
      }
      this.processing = true;
      Object.keys(this.selected).forEach(key => {
        const followupId = this.choiceById(key).followup_id;
        if (followupId && !this.pendingQuestionIds.includes(followupId)) {
          this.pendingQuestionIds.push(followupId);
        }
      });
      const response = Object.assign({}, this.selected, this.freeformResponses);
      const shouldFireWishlistEvent = this.handleAddToWishlistEvent(response, true);
      await this.$axios.$post(`/api/member-survey/${this.question.id}/response`, {
        response,
        fb_wishlist_event: shouldFireWishlistEvent || undefined
      });
      this.processing = false;
      if (shouldFireWishlistEvent) {
        this.handleAddToWishlistEvent(response);
      }
      this.$analyticsHub.track('Member Survey Question Answered', {
        member_id: (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id,
        question_id: this.question.id,
        response: JSON.stringify(response)
      });
      // always show the credit band question last
      if (this.pendingQuestionIds.length === 0 && this.creditBandQuestion && this.question.id !== CREDIT_BAND_SURVEY_ID) {
        this.pendingQuestionIds.push(CREDIT_BAND_SURVEY_ID);
      }
      if (this.pendingQuestionIds.length === 0) {
        // the reason for skipComplete is that the application page's submit method calls this submit method.
        // without skipComplete this would trigger the parent submit again, which would trigger this again, etc
        if (!skipComplete) {
          this.$emit('complete');
        }
        return true;
      } else {
        await this.$fetch();
        this.selected = {};
        this.freeformResponses = {};
        return false;
      }
    },
    handleAddToWishlistEvent(response, returnOnly = false) {
      var _a;
      const minMortgageTimelineRangeForEvent = [MORTGAGE_ANSWER_CHOICE_IDS.makingOffers, MORTGAGE_ANSWER_CHOICE_IDS.from0to3months, MORTGAGE_ANSWER_CHOICE_IDS.from3to6months];
      const isMortgageQuestion = `${this.question.id}` === `${MORTGAGE_QUESTION_ID}`;
      const selectedMortgageTimelineAnswer = Object.entries(response).find(([choiceId, answerValue]) => minMortgageTimelineRangeForEvent.includes(choiceId) && answerValue === true);
      const shouldFireWishlistEvent = isMortgageQuestion && !!selectedMortgageTimelineAnswer;
      const answerChoiceId = selectedMortgageTimelineAnswer === null || selectedMortgageTimelineAnswer === void 0 ? void 0 : selectedMortgageTimelineAnswer[0];
      const answerLabel = (_a = this.question.choices.find(c => `${c.id}` === `${answerChoiceId}`)) === null || _a === void 0 ? void 0 : _a.title;
      if (shouldFireWishlistEvent && !returnOnly) {
        const eventID = uuidV4();
        const eventParams = {
          content_name: answerLabel,
          content_category: 'Mortgage Timeline'
        };
        const fbArgs = ['AddToWishlist', {
          ...eventParams
        }, {
          eventID
        }];
        console.log('AddToWishlist triggered', fbArgs);
        this.$fb.track(...fbArgs);
        this.$axios.$post('/api/fbevent', {
          // CAPI
          eventName: fbArgs[0],
          member: {
            ...this.memberProfile,
            external_id: this.$cookies.get('ajs_anonymous_id')
          },
          eventParams,
          eventId: eventID
        }).catch(err => {
          var _a;
          console.log(`[fbEvent] AddToWishlist CAPI failed for mid: ${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id} with error: ${err}`);
        });
      }
      return shouldFireWishlistEvent;
    }
  }
});